√
<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import MultiSelectDropDown from 'Common/components/elements/input/MultiSelectDropDown.vue'
import AppFiltersSize from 'Modules/AppFiltersSize.vue'
import { useFilterStore } from 'Store/filterStore'
import { useFilterMetaDataQuery } from 'API/queries/iris/filter'
import type { RouteRecordName } from 'vue-router'
import { DefectTypes, DefectStatusTypes } from './types/pages/imageReview/enums'
import { ImageFilters } from 'Modules/types/filterTypes'
import { JeteyeRoutes } from 'Modules/RouterSetup'

const props = defineProps({
  isVisible: {
    type: Boolean,
    default: true,
  },
})

enum AppFilterTypes {
  SubjectModel = 'SubjectModel',
  Aircraft = 'Aircraft',
  InspectionDefinition = 'InspectionDefinition',
  WorkcardDefinition = 'WorkcardDefinition',
  Type = 'Type',
  Severity = 'Severity',
  Size = 'Size',
  Image = 'Image',
}

const route = useRoute()
const filterMetaData = useFilterMetaDataQuery()
const filterStore = useFilterStore()
// Note: We use the store model directly in the toggle function to ensure reactivity
const filterState = filterStore.filtersModel

const pageFilterMapping = {
  [JeteyeRoutes.Home]: [
    AppFilterTypes.SubjectModel,
    AppFilterTypes.Aircraft,
    AppFilterTypes.InspectionDefinition,
    AppFilterTypes.Type,
    AppFilterTypes.Severity,
    AppFilterTypes.Size,
  ],
  [JeteyeRoutes.Aircraft]: [
    AppFilterTypes.InspectionDefinition,
    AppFilterTypes.Type,
    AppFilterTypes.Severity,
    AppFilterTypes.Size,
  ],
  [JeteyeRoutes.SummaryReport]: [
    AppFilterTypes.WorkcardDefinition,
    AppFilterTypes.Type,
    AppFilterTypes.Severity,
    AppFilterTypes.Size,
  ],
  [JeteyeRoutes.ImageReview]: [
    AppFilterTypes.WorkcardDefinition,
    AppFilterTypes.Type,
    AppFilterTypes.Severity,
    AppFilterTypes.Size,
    AppFilterTypes.Image,
  ],
}

const isFilterShowing = (filterType: AppFilterTypes, routeName: RouteRecordName | null | undefined): boolean => {
  if (!routeName) return false
  const pageFilterOptions = pageFilterMapping[routeName as string]
  if (!pageFilterOptions || !Array.isArray(pageFilterOptions)) return false
  return pageFilterOptions.includes(filterType)
}

const getDefectTypeFilterOptions = () => {
  return Object.entries(DefectTypes).map(([key, type]) => {
    return {
      label: type,
      value: key,
    }
  })
}

const getDefectStatusFilterOptions = () => {
  return Object.entries(DefectStatusTypes).map(([key, status]) => {
    return {
      label: status,
      value: key,
    }
  })
}
const subjectModelMeta = computed(() => filterMetaData.data?.value?.subjectModelMetadata)
const aircraftMeta = computed(() => filterMetaData.data?.value?.aircraftMetadata || [])
const inspectionDefinitionMeta = computed(() => filterMetaData.data?.value?.inspectionDefinitionMetadata || [])
const workcardDefinitionMeta = computed(() => filterMetaData.data?.value?.workcardDefinitionMetadata || [])

const subjectModelOptions = computed(() => {
  return subjectModelMeta.value?.map((subjectModel) => {
    return {
      label: subjectModel.name,
      value: subjectModel.uuid,
    }
  })
})

const aircraftOptions = computed(() => {
  return aircraftMeta.value?.map((aircraft) => {
    return {
      label: aircraft.tailNum,
      value: aircraft.uuid,
    }
  })
})

const inspectionDefinitionOptions = computed(() => {
  return inspectionDefinitionMeta.value?.map((definition) => {
    return {
      label: definition.title,
      value: definition.uuid,
    }
  })
})

const workcardDefinitionOptions = computed(() => {
  return workcardDefinitionMeta.value?.map((definition) => {
    return {
      label: definition.title,
      value: definition.uuid,
    }
  })
})

const filterItems = {
  type: getDefectTypeFilterOptions(),
  severity: getDefectStatusFilterOptions(),
  image: [
    {
      label: 'Suggestions (Alt + S)',
      value: ImageFilters.Suggestions,
    },
    {
      label: 'Measurements',
      value: ImageFilters.Measurements,
    },
    {
      label: 'Completed',
      value: ImageFilters.Completed,
    },
  ],
}

const toggleSuggestionsFilter = () => {
  // Use store directly to ensure we have the latest reference
  const currentModel = filterStore.filtersModel

  if (!currentModel.image) {
    currentModel.image = []
  }

  const targetValue = ImageFilters.Suggestions
  const index = currentModel.image.indexOf(targetValue)

  if (index > -1) {
    currentModel.image = currentModel.image.filter((val) => val !== targetValue)
  } else {
    currentModel.image = [...currentModel.image, targetValue]
  }
}

const handleKeydown = (event: any) => {
  const target = event.target as HTMLElement
  if (['INPUT', 'TEXTAREA'].includes(target.tagName) || target.isContentEditable) {
    return
  }

  //Check for Alt + S
  if (event.altKey && event.code === 'KeyS') {
    event.preventDefault()
    event.stopPropagation()

    if (isFilterShowing(AppFilterTypes.Image, route.name)) {
      toggleSuggestionsFilter()
    }
  }
}

onMounted(() => {
  window.addEventListener('keydown', handleKeydown, { capture: true })
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown, { capture: true })
})
</script>

<template>
  <div v-if="isVisible" class="h-full w-full bg-zinc-400 flex items-center justify-start text-zinc-900 px-6 gap-2">
    <template v-if="isFilterShowing(AppFilterTypes.SubjectModel, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.subjectModel"
          label="Aircraft"
          :items="subjectModelOptions"
          :multiple="true"
        />
      </div>
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.Aircraft, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.aircraft"
          label="Tail Number"
          :items="aircraftOptions"
          :multiple="true"
        />
      </div>
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.InspectionDefinition, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.inspectionDefinition"
          label="Inspection"
          :items="inspectionDefinitionOptions"
          :multiple="true"
        />
      </div>
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.WorkcardDefinition, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.workcardDefinition"
          label="Workcard"
          :items="workcardDefinitionOptions"
          :multiple="true"
        />
      </div>
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
    <template
      v-if="
        isFilterShowing(AppFilterTypes.Type, route.name) ||
        isFilterShowing(AppFilterTypes.Severity, route.name) ||
        isFilterShowing(AppFilterTypes.Size, route.name)
      "
    >
      <span class="px-1">Discrepancy</span>
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.Type, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.type"
          label="Type"
          :items="filterItems.type"
          :multiple="true"
          :default-all="true"
        />
      </div>
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.Severity, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.severity"
          label="Severity"
          :items="filterItems.severity"
          :multiple="true"
        />
      </div>
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.Size, route.name)">
      <div class="w-[10rem]">
        <AppFiltersSize v-model:sizes="filterState.sizes" label="Size" />
      </div>
    </template>
    <template
      v-if="
        isFilterShowing(AppFilterTypes.Type, route.name) ||
        isFilterShowing(AppFilterTypes.Severity, route.name) ||
        isFilterShowing(AppFilterTypes.Size, route.name)
      "
    >
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
    <template v-if="isFilterShowing(AppFilterTypes.Image, route.name)">
      <div class="w-[10rem]">
        <MultiSelectDropDown
          v-model:selected-items="filterState.image"
          label="Image"
          :items="filterItems.image"
          :multiple="true"
        />
      </div>
      <el-divider class="h-full text-gray-700" direction="vertical" />
    </template>
  </div>
</template>

<style>
.el-divider.el-divider--vertical {
  height: 100%;
  border: 1px solid lightgray;
}
</style>
